import React from "react";
import { Box, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RadarIcon from "@mui/icons-material/Radar";
import { useNavigate } from "react-router-dom";

import useModules from "../../../hooks/useModules";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";

function TradeShowTile({ icon, title, description, onClick }) {
  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 380,
        aspectRatio: "1 / 1",
        border: `2px solid ${BRAND_BLUE}`,
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.05), 0 8px 20px rgba(15,23,42,0.06)",
        overflow: "hidden",
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <CardContent
          sx={{
            p: 3,
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: 2 }}>
            <Box
              sx={{
                width: 82,
                height: 82,
                borderRadius: "16px",
                backgroundColor: "rgba(100,131,21,0.1)",
                color: BRAND_GREEN,
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              {icon}
            </Box>
          </Box>
          <Typography sx={{ color: BRAND_BLUE, fontSize: 24, fontWeight: 800, mb: 1 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#5c718f", fontSize: 14, lineHeight: 1.6, maxWidth: 280, mx: "auto" }}>
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function TradeShowsLanding() {
  const navigate = useNavigate();
  const { hasComponent } = useModules();
  const canUseTradeShowAgent = hasComponent("prospecting.trade_show_agent");

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2, md: 3 }, py: 1.75 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              background: BRAND_GREEN,
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto",
            }}
          >
            <EventIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND_BLUE, fontSize: 23, fontWeight: 800, lineHeight: 1.05 }}>
              Trade Shows
            </Typography>
            <Typography sx={{ color: "#5c718f", fontSize: 13, mt: 0.35 }}>
              Search trade shows or manage agent workspaces
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pt: { xs: 2.5, md: 3 },
          pb: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1220 }}>
          <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: 4 }}>
            <Typography sx={{ color: BRAND_BLUE, fontSize: { xs: 24, md: 30 }, fontWeight: 800, lineHeight: 1.15, mb: 1 }}>
              Trade Show Intelligence
            </Typography>
            <Typography sx={{ color: "#5c718f", fontSize: 15.5, lineHeight: 1.65 }}>
              Use the Trade Shows component to discover relevant events, save shows for your team, and launch agent workspaces for exhibitor research, scoring, briefs, and outreach preparation.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, 380px)",
                lg: canUseTradeShowAgent ? "repeat(3, 380px)" : "repeat(2, 380px)",
              },
            justifyContent: "center",
            justifyItems: "center",
            gap: 3,
            width: "100%",
          }}
          >
            <TradeShowTile
              icon={<CalendarMonthIcon sx={{ fontSize: 30 }} />}
              title="Trade Show Database"
              description="Search the trade show database, filter events, save selected shows, and export results."
              onClick={() => navigate("/tradeshows/database")}
            />
            {canUseTradeShowAgent && (
              <TradeShowTile
                icon={<SmartToyIcon sx={{ fontSize: 30 }} />}
                title="Trade Show Agent"
                description="Create and manage workspaces for exhibitor research, scoring, briefs, and outreach preparation."
                onClick={() => navigate("/trade-show-agent")}
              />
            )}
            <TradeShowTile
              icon={<RadarIcon sx={{ fontSize: 30 }} />}
              title="Trade Show Radar"
              description="Look up companies and see trade shows where they are likely to exhibit, with confidence grades and source links."
              onClick={() => navigate("/trade-show-radar")}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
